'use client';
import React from 'react';
import addDays from 'date-fns/addDays';

import Layout from '../lib/components/Layout';
import CalendarService from '../lib/services/CalendarService';
import ColumnsDefinition from './ColumnsDef';
import { formatRequestDate, injectLogos } from '../lib/utils';
import { fetchEarnings } from './Services';

import { ICalendarProps, IDateRange } from '../lib/interfaces';
import { Earnings } from '@benzinga/calendar-manager';

export const EarningsCalendarColumnsDefinition = ColumnsDefinition;
export const fetchEarningsData = fetchEarnings;

export const EarningsCalendar: React.FC<ICalendarProps> = ({
  calendar,
  calendarData,
  hasStockTableStyling,
  height,
  hideFilters,
  initialData,
  initialDateRange,
  initialTicker,
  leftSlot,
  onReady,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [tableData, setTableData] = React.useState<Earnings[]>(initialData || []);
  let dateRange: IDateRange;

  if (initialDateRange) {
    dateRange = initialDateRange;
  } else {
    dateRange = {
      date_from: formatRequestDate(new Date()),
      date_to: formatRequestDate(addDays(new Date(), 7)),
    };
  }

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setTableData(initialData);
    }
  }, [initialData, calendarData?.serverSide]);

  function onTableReady() {
    if (initialData) {
      setTableData(initialData);
    } else {
      const params = {
        'parameters[date_from]': dateRange.date_from,
        'parameters[date_to]': dateRange.date_to,
      };
      if (initialTicker) {
        params['parameters[tickers]'] = initialTicker;
      }
      fetchData(params);
    }

    if (onReady) {
      setTimeout(() => onReady());
    }
  }

  function fetchData(filterParams: any) {
    const query = {
      ...filterParams,
      pagesize: 1000,
    };

    setLoading(true);

    const Calendar = new CalendarService();

    Calendar.getEarnings(query)
      .then(async ({ data }: any) => {
        if (data && data.earnings) {
          const { earnings } = data;
          earnings.sort((a: any, b: any) => {
            return parseInt(b.revenue_est, 10) - parseInt(a.revenue_est, 10);
          });
          const earningsWithLogos = await injectLogos(earnings);
          setTableData(earningsWithLogos ?? earnings);
        }
      })
      .finally(() => setLoading(false));
  }

  return (
    <Layout
      calendar={calendar}
      calendarData={calendarData}
      columnDefs={ColumnsDefinition}
      enableSearch
      hasStockTableStyling={hasStockTableStyling}
      height={height}
      hideFilters={hideFilters}
      initialDateRange={dateRange}
      isGated={true}
      leftSlot={leftSlot}
      onFilterChanged={fetchData}
      onTableReady={onTableReady}
      tableData={tableData}
      tableLoading={loading}
      ticker={initialTicker}
    />
  );
};
